import React, { useCallback, useState } from "react";
import { Profile } from "@/store/models/users.models";
import { Pagination, Skeleton } from "@mui/material";
import {
  IMedia,
  IMediaVideoWithMeta,
  IWatchedMediaWithMeta,
} from "@/store/models/video.models";
import EditVideoModal from "@/pages/ContentPage/components/EditVideoModal/EditVideoModal";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import styles from "./LikedVideos.module.scss";
import { VideoCard } from "@/components/VideoCard/VideoCard";
import useItemsPerPage from "@/hooks/useItemsPerPage";
import {
  useDeleteMediaMutation,
  useGetMediaVideoQuery,
  useGetWatchedMediaQuery,
  useUpdateMediaMutation,
} from "@/store/rtk-api/media.api";

type Props = {
  profile: Profile;
  type: "liked" | "watched";
};

const LikedVideos = ({ profile, type }: Props) => {
  const [page, setPage] = useState(1);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<IMedia | null>(null);
  const itemsPerPage = useItemsPerPage(2, 2, 3, 1280, 1680);

  const {
    data: likedVideoData,
    isLoading: isLikedLoading,
    isSuccess: isLikedSuccess,
  } = useGetMediaVideoQuery({
    page,
    limit: itemsPerPage,
    sortByDate: "ASC",
    profileUid: profile.uid,
    isFavourite: true,
  });

  const {
    data: watchedVideoData,
    isLoading: isWatchedLoading,
    isSuccess: isWatchedSuccess,
    refetch,
  } = useGetWatchedMediaQuery({
    page,
    limit: itemsPerPage,
    profileUid: profile.uid,
    mediaType: "video",
  });

  const isLoading = type === "liked" ? isLikedLoading : isWatchedLoading;
  const isSuccess = type === "liked" ? isLikedSuccess : isWatchedSuccess;
  const videoData: IMediaVideoWithMeta | IWatchedMediaWithMeta | undefined =
    type === "liked" ? likedVideoData : watchedVideoData;

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleToggleEditModal = (video: IMedia) => {
    setSelectedVideo(video);
    setIsOpenEditModal(true);
  };

  const handleToggleDeleteModal = (video: IMedia) => {
    setSelectedVideo(video);
    setIsOpenDeleteModal(true);
  };

  const [deleteVideoByUid] = useDeleteMediaMutation();
  const [editVideo] = useUpdateMediaMutation();

  const handleDeleteVideo = useCallback(async () => {
    if (selectedVideo) {
      await deleteVideoByUid(selectedVideo.uid);
      setIsOpenDeleteModal(false);
    }
  }, [deleteVideoByUid, selectedVideo]);

  const handleEditVideo = useCallback(
    async (formData: FormData) => {
      await editVideo(formData);
      setIsOpenEditModal(false);
      refetch();
    },
    [editVideo, refetch]
  );

  const videoList =
    type === "liked"
      ? (videoData as IMediaVideoWithMeta)?.media
      : (videoData as IWatchedMediaWithMeta)?.data;

  return (
    <div className={styles.container}>
      <div className={styles.itemsWithPagination}>
        <div className={styles.itemsContainer}>
          {isLoading &&
            Array(3)
              .fill(0)
              .map((_, index) => (
                <div className={styles.item} key={index}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height="200px"
                    className={styles.skeletonItem}
                  />
                  <Skeleton
                    width="60%"
                    height="30px"
                    className={styles.skeletonItem}
                  />
                  <Skeleton
                    width="100%"
                    height="100px"
                    className={styles.skeletonItem}
                  />
                </div>
              ))}
          {isSuccess && videoList?.length === 0 && (
            <div className={styles.noDataText}>
              {type === "liked"
                ? "There is no data about favorite videos"
                : "There is no data about watched videos"}
            </div>
          )}
          {videoData &&
            videoList &&
            videoList.map((video) => (
              <VideoCard
                key={video.uid}
                video={video}
                handleToggleEditModal={handleToggleEditModal}
                handleToggleDeleteModal={handleToggleDeleteModal}
                isEdit={true}
              />
            ))}
          {isOpenEditModal && (
            <EditVideoModal
              isOpenEditModal={isOpenEditModal}
              setIsOpenEditModal={setIsOpenEditModal}
              selectedVideo={selectedVideo}
              setSelectedVideo={setSelectedVideo}
              handleEditVideo={handleEditVideo}
            />
          )}
          {isOpenDeleteModal && (
            <DeleteModal
              isOpen={isOpenDeleteModal}
              setIsOpen={setIsOpenDeleteModal}
              name={selectedVideo ? selectedVideo.title : ""}
              supportingText="Are you sure you want to delete this video?"
              onDelete={handleDeleteVideo}
            />
          )}
        </div>
        {videoData && videoData.total > 0 && (
          <Pagination
            page={page}
            onChange={handlePageChange}
            count={Math.ceil(videoData.total / itemsPerPage)}
          />
        )}
      </div>
    </div>
  );
};

export default LikedVideos;

